/**
 * Admin → Media: Tammy's downloadable / shareable content library.
 *
 * Shows everything we've created for her (TV slides, flyers, social, print).
 * Full-res originals live in Cloudflare R2; small thumbnails come from Supabase.
 * Each item has Download (full-res) + Copy share link, plus self-service upload.
 *
 * Auth: portal login session via adminFetch (same as Contact messages).
 */

import { useEffect, useRef, useState } from 'react';
import {
  HiDownload, HiLink, HiTrash, HiUpload, HiPhotograph, HiDocument, HiFilm,
} from 'react-icons/hi';
import { adminFetch } from '../lib/adminApi';

const API_BASE = import.meta.env.VITE_API_URL || '';

function api(path: string) {
  return API_BASE ? `${API_BASE}${path}` : path;
}

interface MediaRow {
  id: string;
  title: string;
  category: string;
  description: string | null;
  file_name: string;
  content_type: string | null;
  size_bytes: number | null;
  thumb_url: string | null;
  storage_provider: string;
  created_at: string;
}

interface StorageStatus { r2: boolean; publicShare: boolean }

/** Human-friendly file size. */
function fmtSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Pick an icon for files that have no image thumbnail. */
function TypeIcon({ type }: { type: string | null }) {
  if (type?.startsWith('video/')) return <HiFilm className="w-10 h-10 text-primary-400" />;
  if (type === 'application/pdf') return <HiDocument className="w-10 h-10 text-primary-400" />;
  return <HiPhotograph className="w-10 h-10 text-primary-400" />;
}

export default function Media() {
  const [items, setItems] = useState<MediaRow[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [storage, setStorage] = useState<StorageStatus>({ r2: false, publicShare: false });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Upload form state
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('TV Slides');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function flash(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2500);
  }

  async function load() {
    setLoading(true);
    try {
      const res = await adminFetch(api('/api/media'));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.media || []);
      setCategories(data.categories || []);
      setStorage(data.storage || { r2: false, publicShare: false });
    } catch (e) {
      flash('Could not load media. Check your connection.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { flash('Choose a file first.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title || file.name);
      fd.append('category', category);
      fd.append('description', description);
      const res = await adminFetch(api('/api/media'), { method: 'POST', body: fd });
      if (!res.ok) throw new Error((await res.json()).error || 'Upload failed');
      setTitle(''); setDescription('');
      if (fileRef.current) fileRef.current.value = '';
      flash('Uploaded!');
      load();
    } catch (e: any) {
      flash(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleDownload(row: MediaRow) {
    setBusyId(row.id);
    try {
      const res = await adminFetch(api(`/api/media/${row.id}/download`));
      if (!res.ok) throw new Error('Failed');
      const { url } = await res.json();
      // Opening the signed URL (with attachment disposition) starts the download.
      window.open(url, '_blank');
      // Confirm to the user — without this the download feels like nothing happened.
      flash('Download started — check your downloads folder.');
    } catch {
      flash('Could not start download.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleShare(row: MediaRow) {
    setBusyId(row.id);
    try {
      const res = await adminFetch(api(`/api/media/${row.id}/share`));
      if (!res.ok) throw new Error('Failed');
      const { url, expires } = await res.json();
      await navigator.clipboard.writeText(url);
      flash(expires ? 'Share link copied (works for 7 days).' : 'Share link copied!');
    } catch {
      flash('Could not create share link.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(row: MediaRow) {
    if (!window.confirm(`Delete "${row.title}"? This cannot be undone.`)) return;
    setBusyId(row.id);
    try {
      const res = await adminFetch(api(`/api/media/${row.id}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed');
      flash('Deleted.');
      setItems((prev) => prev.filter((i) => i.id !== row.id));
    } catch {
      flash('Could not delete.');
    } finally {
      setBusyId(null);
    }
  }

  const grouped = categories
    .map((cat) => ({ cat, rows: items.filter((i) => i.category === cat) }))
    .filter((g) => g.rows.length > 0);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Media</h1>
        <p className="text-gray-500">Download or share the content we've created for you.</p>
      </div>

      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg">
          {toast}
        </div>
      )}

      {!storage.r2 && (
        <div className="mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2">
          High-res storage (Cloudflare R2) isn't connected yet — originals are temporarily on Supabase.
          Everything still downloads and shares normally.
        </div>
      )}

      {/* Upload */}
      <form onSubmit={handleUpload} className="bg-white border border-gray-200 rounded-xl p-4 mb-8 grid gap-3 sm:grid-cols-2">
        <input
          type="text" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (e.g. TV Slide — Custom Cakes)"
          className="border border-gray-300 rounded-lg px-3 py-2"
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2">
          {(categories.length ? categories : ['TV Slides', 'Flyers', 'Social', 'Print', 'Other']).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <input
          type="text" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Short note (optional)"
          className="border border-gray-300 rounded-lg px-3 py-2 sm:col-span-2"
        />
        <input ref={fileRef} type="file" accept="image/*,application/pdf,video/mp4"
          className="text-sm sm:col-span-1" />
        <button type="submit" disabled={uploading}
          className="btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <HiUpload className="w-5 h-5" /> {uploading ? 'Uploading…' : 'Upload media'}
        </button>
      </form>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : grouped.length === 0 ? (
        <p className="text-gray-500">No media yet. Upload your first file above.</p>
      ) : (
        grouped.map(({ cat, rows }) => (
          <section key={cat} className="mb-8">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">{cat}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map((row) => (
                <div key={row.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
                  <div className="aspect-video bg-gray-50 flex items-center justify-center overflow-hidden">
                    {row.thumb_url
                      ? <img src={row.thumb_url} alt={row.title} className="w-full h-full object-cover" />
                      : <TypeIcon type={row.content_type} />}
                  </div>
                  <div className="p-3 flex flex-col gap-2 flex-1">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 leading-tight">{row.title}</p>
                      <p className="text-xs text-gray-400 mt-1">{row.file_name} · {fmtSize(row.size_bytes)}</p>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <button onClick={() => handleDownload(row)} disabled={busyId === row.id}
                        className="flex-1 inline-flex items-center justify-center gap-1 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg px-3 py-2 disabled:opacity-60">
                        <HiDownload className="w-4 h-4" /> Download
                      </button>
                      <button onClick={() => handleShare(row)} disabled={busyId === row.id}
                        title="Copy share link"
                        className="inline-flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-3 py-2">
                        <HiLink className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(row)} disabled={busyId === row.id}
                        title="Delete"
                        className="inline-flex items-center justify-center bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 rounded-lg px-3 py-2">
                        <HiTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
